import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Users, Eye, Target, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const PAGE_LABEL = (p) => {
  if (!p || p === "/") return "Home";
  return p.replace(/^\//, "").replace(/\/$/, "").replace(/-/g, " ").replace(/\//g, " › ") || "Home";
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-slate-400 text-sm">{label}</span>
      <span className="rounded-lg p-2" style={{ background: `${accent}22`, color: accent }}><Icon className="h-5 w-5" /></span>
    </div>
    <p className="font-display text-3xl text-white mt-2 truncate">{value}</p>
  </div>
);

const RankBars = ({ rows, keyName, labelFn, color, testid, emptyText }) => {
  const max = Math.max(1, ...rows.map((r) => r[keyName]));
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r, i) => {
        const label = labelFn(r);
        return (
          <div key={i}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-200 capitalize truncate pr-3">{label}</span>
              <span className="font-semibold" style={{ color }}>{r[keyName]}</span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(r[keyName] / max) * 100}%`, background: color }} />
            </div>
          </div>
        );
      })}
      {rows.length === 0 && <p className="text-slate-500 text-sm">{emptyText}</p>}
    </div>
  );
};

const AdminAnalytics = () => {
  const [range, setRange] = useState("daily");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.traffic(range).then((d) => { if (alive) { setData(d); setLoading(false); } }).catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
  }, [range]);

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-display text-3xl text-white">Visitor Analytics</h1>
        <div className="flex rounded-full border border-white/15 bg-[#0e2740] p-1" data-testid="range-toggle">
          {["daily", "monthly"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={`px-4 py-1.5 rounded-full text-sm font-heading capitalize transition-colors ${range === r ? "bg-[#f5a524] text-[#0a1f33]" : "text-slate-300 hover:text-white"}`}
            >
              {r === "daily" ? "Daily · 30d" : "Monthly · 12mo"}
            </button>
          ))}
        </div>
      </div>
      <p className="text-slate-400 text-sm mb-6">Where visitors land on your site and where they come from.</p>

      {loading || !data ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#f5a524]" /></div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatCard icon={Users} label="Visits" value={data.total_visits} accent="#f5a524" />
            <StatCard icon={Eye} label="Page views" value={data.total_pageviews} accent="#1c6ba3" />
            <StatCard icon={Target} label="Leads" value={data.total_leads ?? 0} accent="#0e9488" />
            <StatCard icon={TrendingUp} label="Conversion" value={`${data.overall_rate ?? 0}%`} accent="#a855f7" />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-5 mb-6">
            <h3 className="font-heading text-lg text-white mb-4">Visits over time · {range === "daily" ? "last 30 days" : "last 12 months"}</h3>
            <div className="h-72" data-testid="visits-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <AreaChart data={data.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visitFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f5a524" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#f5a524" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: "#ffffff20" }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
                  <Tooltip
                    contentStyle={{ background: "#071726", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }}
                    labelStyle={{ color: "#f5a524" }}
                    cursor={{ stroke: "#f5a524", strokeOpacity: 0.3 }}
                    formatter={(v) => [v, "Visits"]}
                  />
                  <Area type="monotone" dataKey="visits" stroke="#f5a524" strokeWidth={2.5} fill="url(#visitFill)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-5">
              <h3 className="font-heading text-lg text-white mb-4">Where people land</h3>
              <RankBars rows={data.landing_pages} keyName="visits" labelFn={(r) => PAGE_LABEL(r.path)} color="#a855f7" testid="landing-pages" emptyText="No landing data yet — visits will appear here." />
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-5">
              <h3 className="font-heading text-lg text-white mb-4">Where they come from</h3>
              <RankBars rows={data.sources} keyName="visits" labelFn={(r) => r.source} color="#0e9488" testid="traffic-sources" emptyText="No source data yet — traffic sources will appear here." />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-5 mt-4" data-testid="conversions-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h3 className="font-heading text-lg text-white">Which sources actually book</h3>
              <span className="text-sm text-slate-400">Visits that became leads · overall <span className="text-[#a855f7] font-semibold">{data.overall_rate ?? 0}%</span></span>
            </div>
            <p className="text-slate-500 text-xs mb-4">Leads are matched to the source that first brought the visitor. Older leads with no source show as "Unknown".</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-left border-b border-white/10">
                    <th className="py-2 font-heading font-normal">Source</th>
                    <th className="py-2 font-heading font-normal w-28 text-right">Visits</th>
                    <th className="py-2 font-heading font-normal w-24 text-right">Leads</th>
                    <th className="py-2 font-heading font-normal w-40">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conversions.map((c, i) => (
                    <tr key={i} className="border-b border-white/5" data-testid={`conv-row-${c.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="py-2.5 text-slate-200 capitalize">{c.source}</td>
                      <td className="py-2.5 text-right text-slate-300">{c.visits}</td>
                      <td className="py-2.5 text-right text-[#0e9488] font-semibold">{c.leads}</td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full rounded-full bg-[#a855f7] transition-all" style={{ width: `${c.rate == null ? 0 : c.rate}%` }} />
                          </div>
                          <span className="text-slate-300 w-12 text-right">{c.rate == null ? "—" : `${c.rate}%`}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.conversions.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-slate-500">No visits or leads in this range yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
